import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Typography,
  Button,
  Row,
  Col,
  Table,
  Spin,
  Alert,
  Space,
  Tag,
} from "antd";
import {
  ArrowLeftOutlined,
  CalculatorOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import LandingLayout from "./LandingLayout";

const { Title, Paragraph, Text } = Typography;

// Shared cash flow view used both in InstrumentDetails (summary) and dedicated page (full)
export const CashFlowView = ({ isin, mode = "full" }) => {
  const baseUrl =
    process.env.REACT_APP_BASE_URL || "http://localhost:8080/api/v1/sb";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isin) {
      fetchCashFlowData(isin);
    }
  }, [isin]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `₹${parseFloat(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const fetchCashFlowData = async (isinToFetch) => {
    if (!isinToFetch || isinToFetch.trim().length === 0) {
      setError("ISIN is required");
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await apiClient.get(
        `${baseUrl}/master-isin/${isinToFetch.trim()}/cash-flow`,
        {
          validateStatus: () => true,
        }
      );

      const result = response.data;

      if (result && (result.statusCode === 200 || result.status === 200)) {
        setData(result.data);
      } else if (
        result &&
        (result.statusCode === 404 || result.status === 404)
      ) {
        setData(null);
        setError(result.message || "Cash flow data not found");
      } else {
        throw new Error(result?.message || "Failed to fetch bond cash flow");
      }
    } catch (e) {
      console.error("Error fetching bond cash flow:", e);
      setError(
        e.response?.data?.message ||
          e.message ||
          "Error fetching bond cash flow"
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Common grouping by series
  const buildSeriesMap = () => {
    const seriesMap = {};
    data.cashFlows.forEach((cf) => {
      const seriesName = cf.seriesName || "Default Series";
      if (!seriesMap[seriesName]) {
        seriesMap[seriesName] = [];
      }
      seriesMap[seriesName].push(cf);
    });
    return seriesMap;
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Spin size="large" />
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">Loading cash flow data...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!data || !data.cashFlows || data.cashFlows.length === 0) {
    return (
      <Card
        style={{
          textAlign: "center",
          padding: "1.5rem",
          borderRadius: 12,
          border: "1px dashed #d1d5db",
        }}
      >
        <CalculatorOutlined
          style={{ fontSize: 32, color: "#9ca3af", marginBottom: 12 }}
        />
        <Title level={5} type="secondary">
          No Cash Flow Data Available
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Cash flow data is not available for this instrument.
        </Paragraph>
      </Card>
    );
  }

  const seriesMap = buildSeriesMap();
  const seriesNames = Object.keys(seriesMap);

  // Summary mode: small stats + per-series chips (used in InstrumentDetails)
  if (mode === "summary") {
    const totalAmount = data.cashFlows.reduce(
      (sum, cf) => sum + (parseFloat(cf.amount) || 0),
      0
    );

    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={8}>
            <Card size="small">
              <Space direction="vertical" size={4}>
                <Text type="secondary">Total Cash Flows</Text>
                <Text strong>{data.cashFlows.length}</Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card size="small">
              <Space direction="vertical" size={4}>
                <Text type="secondary">Series Count</Text>
                <Text strong>{seriesNames.length}</Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card size="small">
              <Space direction="vertical" size={4}>
                <Text type="secondary">Total Amount</Text>
                <Text strong>{formatCurrency(totalAmount)}</Text>
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          {seriesNames.map((seriesName) => {
            const seriesFlows = seriesMap[seriesName];
            return (
              <Col xs={24} sm={12} md={8} key={seriesName}>
                <Card size="small">
                  <Tag color="blue" style={{ marginBottom: 4 }}>
                    {seriesName}
                  </Tag>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {seriesFlows.length} cash flow entries
                    </Text>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  }

  // Full mode: detailed tables by series (used in dedicated page)
  const cashFlowColumns = [
    {
      title: "Series Name",
      dataIndex: "seriesName",
      key: "seriesName",
      width: 200,
      render: (text) => <Text strong>{text || "N/A"}</Text>,
    },
    {
      title: "Cash Flow Type",
      dataIndex: "cashFlows",
      key: "cashFlows",
      width: 150,
    },
    {
      title: "Coupon Date",
      dataIndex: "couponDate",
      key: "couponDate",
      width: 120,
      render: (text) => text || "N/A",
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 120,
      render: (text) => text || "N/A",
    },
    {
      title: "Day Count",
      dataIndex: "dayCount",
      key: "dayCount",
      width: 120,
      render: (text) => text || "N/A",
    },
    {
      title: "Days in Period",
      dataIndex: "noOfDaysInCouponPeriod",
      key: "noOfDaysInCouponPeriod",
      align: "center",
      width: 120,
      render: (value) => value || "N/A",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 150,
      render: (value) => formatCurrency(value),
    },
  ];

  return (
    <>
      {seriesNames.map((seriesName) => {
        const seriesFlows = seriesMap[seriesName];
        const totalAmount = seriesFlows.reduce(
          (sum, cf) => sum + (parseFloat(cf.amount) || 0),
          0
        );

        return (
          <Card
            key={seriesName}
            title={
              <Space>
                <DollarOutlined />
                <span>{seriesName}</span>
                <Tag color="blue">{seriesFlows.length} entries</Tag>
                <Tag color="green">Total: {formatCurrency(totalAmount)}</Tag>
              </Space>
            }
            style={{
              marginBottom: 24,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
            }}
          >
            <Table
              columns={cashFlowColumns}
              dataSource={seriesFlows.map((cf, index) => ({
                key: `${seriesName}-${index}`,
                ...cf,
              }))}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} cash flows`,
              }}
              scroll={{ x: true }}
              size="middle"
            />
          </Card>
        );
      })}
    </>
  );
};

// Dedicated page wrapper: uses CashFlowView in full mode
const BondCashFlowPage = () => {
  const navigate = useNavigate();
  const { isin: isinParam } = useParams();

  const content = (
    <div className="bond-detail-container">
      <div className="container" style={{ paddingTop: 24, paddingBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </div>

        <Card
          className="modern-section-card"
          style={{
            marginBottom: 24,
            borderRadius: 18,
            border: "1px solid #e5e7eb",
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.06)",
            background: "#ffffff",
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Row gutter={[24, 16]} align="middle">
            <Col xs={24} md={16}>
              <div style={{ marginBottom: 8 }}>
                <Text
                  style={{
                    fontSize: 13,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: "#6b7280",
                  }}
                >
                  Bond Analytics
                </Text>
              </div>
              <Title
                level={2}
                style={{ margin: 0, color: "#111827", letterSpacing: 0.3 }}
              >
                Bond Cash Flow Details
              </Title>
              <Paragraph
                style={{
                  marginTop: 12,
                  marginBottom: 0,
                  color: "#4b5563",
                  maxWidth: 640,
                }}
              >
                View detailed cash flow schedule for ISIN:{" "}
                <Text strong>{isinParam || "N/A"}</Text>
              </Paragraph>
            </Col>
            <Col xs={24} md={8}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 8,
                }}
              >
                <Tag
                  color="geekblue"
                  style={{
                    borderRadius: 999,
                    padding: "4px 16px",
                    fontWeight: 500,
                  }}
                >
                  <CalculatorOutlined /> Calculator
                </Tag>
              </div>
            </Col>
          </Row>
        </Card>

        <CashFlowView isin={isinParam} mode="full" />
      </div>
    </div>
  );

  return <LandingLayout>{content}</LandingLayout>;
};

export default BondCashFlowPage;

